import { Form, FormInstance, Select } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { AppPartType } from "../../AppForm/types";
import { useFetchEmployeeInfoChildrenSelection } from "@lhd/hooks";
import {
  addDeletedOtionps,
  findEmployeeInfoParentPart,
  getConfigAppPartType,
  getEmployeeInfoChildCurrentOptionsKey,
  getEmployeeInfoChildDisplayLabel,
  getEmployeeInfoChildrenOptions,
  getEmployeeInfoParentFormKey,
  getParamUrlFetchEmployeeInfoChildrenSelection,
  parseEmployeeInfoChildCurrentOptions,
  parseEmployeeInfoChildFormValue,
  parseEmployeeInfoFormValue,
  stringifyEmployeeInfoChildCurrentOptions,
  stringifyEmployeeInfoChildFormValue,
} from "../../utils";
import { Styles } from "./styled";

interface IProps {
  isDisable?: boolean;
  preview?: boolean;
  item: AppPartType;
  parentItem?: AppPartType;
  style?: React.CSSProperties;
  isTable?: boolean;
  nameItem: string;
  parentNameItem?: string;
  styleColor?: React.CSSProperties;
  className?: string;
  appParts?: AppPartType[];
  form?: FormInstance<any>;
  onChange?: (value?: string, currentOptions?: string) => void;
}

interface ISelectProps {
  value?: string | string[];
  onChange?: (value?: string[]) => void;
  extraOnChange?: (value?: string, currentOptions?: string) => void;
  workerId?: string;
  appPartType?: string;
  disabled?: boolean;
  preview?: boolean;
  currentOptions?: string;
}

export const EmployeeInfoChildSelect = ({
  value,
  onChange,
  extraOnChange,
  workerId,
  appPartType,
  disabled,
  preview,
  currentOptions,
}: ISelectProps) => {
  const [opened, setOpened] = useState(false);
  const onChangeRef = useRef(onChange);
  const extraOnChangeRef = useRef(extraOnChange);
  onChangeRef.current = onChange;
  extraOnChangeRef.current = extraOnChange;
  const savedOptions = useMemo(
    () => parseEmployeeInfoChildCurrentOptions(currentOptions),
    [currentOptions],
  );
  const selectedIds = useMemo(
    () => parseEmployeeInfoChildFormValue(value),
    [value],
  );
  const fetchConfig = workerId
    ? getParamUrlFetchEmployeeInfoChildrenSelection({
        appPartType: appPartType || "",
        workerId,
      })
    : undefined;

  const { data, isFetching } = useFetchEmployeeInfoChildrenSelection({
    url: fetchConfig?.url || "",
    params: fetchConfig?.params,
    enabled: opened && !!workerId && !!fetchConfig?.url && !preview,
  });

  const fetchedOptions = useMemo(
    () => getEmployeeInfoChildrenOptions(data, appPartType),
    [data, appPartType],
  );
  const rawOptions = fetchedOptions.length ? fetchedOptions : savedOptions;

  const options = addDeletedOtionps(selectedIds, rawOptions);

  useEffect(() => {
    setOpened(false);
  }, [workerId]);

  const emitChange = (nextValues?: string[]) => {
    const ids = parseEmployeeInfoChildFormValue(nextValues);
    const matched = options.filter((option: any) =>
      ids.includes(String(option.value)),
    );
    const currentOptionsJson = stringifyEmployeeInfoChildCurrentOptions(
      matched.map((option: any) => ({
        ...option,
        worker_id: workerId,
      })),
    );
    const serialized = ids.length
      ? stringifyEmployeeInfoChildFormValue(ids)
      : undefined;
    onChangeRef.current?.(ids.length ? ids : undefined);
    extraOnChangeRef.current?.(serialized, currentOptionsJson);
  };

  return (
    <Select
      mode="multiple"
      showSearch
      allowClear
      style={{ width: "100%" }}
      disabled={disabled || !workerId}
      loading={isFetching}
      placeholder="選択してください"
      value={selectedIds}
      optionFilterProp="label"
      onDropdownVisibleChange={(open) => {
        if (open) setOpened(true);
      }}
      onChange={(nextValue) => emitChange(nextValue as string[])}
      filterOption={(input, option: any) =>
        String(option?.label ?? option?.children ?? "")
          .toLowerCase()
          .includes(input.trim().toLowerCase())
      }
    >
      {options.map((option: any) => (
        <Select.Option key={String(option.value)} value={String(option.value)}>
          {option.label}
        </Select.Option>
      ))}
    </Select>
  );
};

const EmployeeInfoChildField = (props: IProps) => {
  const {
    item,
    nameItem,
    parentNameItem: parentNameItemProp,
    appParts,
    form: formProp,
    onChange,
    style,
    preview,
    isTable,
    isDisable,
    styleColor = {},
    className,
  } = props;
  const form = Form.useFormInstance() || formProp;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const { isHiddenLabel, labelText } = getConfigAppPartType(item);
  const isShowOnlyValue = !preview && isTable && isDisable;
  const currentOptionsName = getEmployeeInfoChildCurrentOptionsKey(nameItem);
  const parentPart = useMemo(
    () => findEmployeeInfoParentPart(item, appParts),
    [item, appParts],
  );
  const parentNameItem =
    parentNameItemProp ||
    getEmployeeInfoParentFormKey(nameItem, item, parentPart);
  const parentValue = Form.useWatch(parentNameItem as any, form);
  const workerId = parseEmployeeInfoFormValue(parentValue);
  const currentValue = Form.useWatch(nameItem, form);
  const currentOptions = Form.useWatch(currentOptionsName, form);
  const selectedLabel = getEmployeeInfoChildDisplayLabel(
    currentValue,
    currentOptions,
  );
  const prevWorkerIdRef = useRef<string | undefined>();

  useEffect(() => {
    if (!nameItem || !form) return;
    const prevWorkerId = prevWorkerIdRef.current;
    prevWorkerIdRef.current = workerId;

    const parentChanged =
      !!prevWorkerId && !!workerId && String(prevWorkerId) !== String(workerId);
    const parentCleared = !!prevWorkerId && !workerId;
    if (!parentChanged && !parentCleared) return;

    form.setFieldValue(nameItem, undefined);
    form.setFieldValue(currentOptionsName, "[]");
    onChangeRef.current?.(undefined, "[]");
  }, [form, nameItem, workerId, currentOptionsName]);

  return (
    <Styles
      style={style}
      color={styleColor?.color}
      background={styleColor?.background}
      className={`full employee-info-child-field ${item.app_part_type}`}
    >
      <div className="value_text_container">
        {isShowOnlyValue && selectedLabel}
      </div>
      <Form.Item name={currentOptionsName} hidden />
      <Form.Item
        hidden={isShowOnlyValue}
        label={
          isHiddenLabel || isTable ? undefined : (
            <div className="no-wrap-text" title={labelText}>
              {labelText}
            </div>
          )
        }
        name={nameItem}
        rules={
          item.is_required_field
            ? [
                {
                  required: true,
                  type: "array",
                  min: 1,
                  message: `必須項目です`,
                },
              ]
            : []
        }
        className={className}
        getValueProps={(val) => ({
          value: parseEmployeeInfoChildFormValue(val),
        })}
        normalize={(val) =>
          Array.isArray(val) && val.length
            ? val.map((item) => String(item))
            : undefined
        }
      >
        <EmployeeInfoChildSelect
          workerId={workerId}
          appPartType={item.app_part_type}
          disabled={isDisable}
          preview={preview}
          currentOptions={currentOptions}
          extraOnChange={(value, nextCurrentOptions) => {
            form.setFieldValue(currentOptionsName, nextCurrentOptions || "[]");
            onChangeRef.current?.(value, nextCurrentOptions);
          }}
        />
      </Form.Item>
    </Styles>
  );
};

export default EmployeeInfoChildField;
